import { Map as MapIcon, Satellite, Sun } from 'lucide-react';
import maplibregl, { type Map as MlMap } from 'maplibre-gl';
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { MAP_STYLE_IDS, OSM_RASTER, resolveMapStyle, useMapStyle, type MapStyleId } from './mapStyles';

export const EGYPT_CENTER: [number, number] = [31.2357, 30.0444];

const MapCtx = createContext<MlMap | null>(null);
export const useMap = () => useContext(MapCtx);

export interface MapViewProps {
  center?: [number, number];
  zoom?: number;
  className?: string;
  children?: ReactNode;
  onReady?: (map: MlMap) => void;
  interactive?: boolean;
  /** Hide the basemap switcher (e.g. small preview maps). */
  showStyleControl?: boolean;
}

/**
 * MapLibre map with RTL text plugin, navigation control on the inline-start side, a basemap switcher
 * (light / streets / satellite, persisted) and a raster fallback. Switching the basemap re-creates the
 * map at the same camera, so child layers re-mount and re-add their sources cleanly.
 */
export function MapView({ center = EGYPT_CENTER, zoom = 6, className, children, onReady, interactive = true, showStyleControl = true }: MapViewProps) {
  const el = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<MlMap | null>(null);
  const [styleId] = useMapStyle();
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  // Camera of the previous map instance, carried over across basemap switches.
  const camera = useRef<{ center: [number, number]; zoom: number; bearing: number; pitch: number } | null>(null);

  useEffect(() => {
    if (!el.current) return;
    if (maplibregl.getRTLTextPluginStatus() === 'unavailable') {
      maplibregl.setRTLTextPlugin('https://unpkg.com/@mapbox/mapbox-gl-rtl-text@0.2.3/mapbox-gl-rtl-text.js', true).catch(() => undefined);
    }
    let cancelled = false;
    let m: MlMap | null = null;
    void resolveMapStyle(styleId).then((style) => {
      if (cancelled || !el.current) return;
      const cam = camera.current;
      m = new maplibregl.Map({ container: el.current, style, center: cam?.center ?? center, zoom: cam?.zoom ?? zoom, bearing: cam?.bearing ?? 0, pitch: cam?.pitch ?? 0, attributionControl: false, interactive });
      m.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-left');
      if (interactive) m.addControl(new maplibregl.NavigationControl({ showCompass: false }), document.documentElement.dir === 'rtl' ? 'top-right' : 'top-left');
      m.once('error', (e) => {
        // Style failed to load → swap to raster OSM
        if (m && !m.isStyleLoaded() && (e as { error?: { status?: number } }).error?.status !== undefined) m.setStyle(OSM_RASTER);
      });
      m.once('load', () => {
        if (!m) return;
        setMap(m);
        onReadyRef.current?.(m);
      });
    });
    return () => {
      cancelled = true;
      if (m) {
        const c = m.getCenter();
        camera.current = { center: [c.lng, c.lat], zoom: m.getZoom(), bearing: m.getBearing(), pitch: m.getPitch() };
        setMap(null);
        m.remove();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [styleId]);

  return (
    <div ref={el} className={`relative ${className ?? 'h-full w-full'}`} dir="ltr">
      {map ? <MapCtx.Provider value={map}>{children}</MapCtx.Provider> : null}
      {interactive && showStyleControl ? <MapStyleControl /> : null}
    </div>
  );
}

const STYLE_ICON: Record<MapStyleId, typeof MapIcon> = { light: Sun, streets: MapIcon, satellite: Satellite };

/** Segmented basemap switcher, placed opposite the navigation control. */
function MapStyleControl() {
  const { t } = useTranslation();
  const [styleId, setStyleId] = useMapStyle();
  const side = document.documentElement.dir === 'rtl' ? 'left-2.5' : 'right-2.5';
  return (
    <div className={`absolute top-2.5 z-[3] flex overflow-hidden rounded-lg border border-border bg-surface/95 shadow-overlay backdrop-blur ${side}`} role="radiogroup" aria-label={t('map.basemap')}>
      {MAP_STYLE_IDS.map((id) => {
        const Icon = STYLE_ICON[id];
        const active = id === styleId;
        return (
          <button
            key={id}
            type="button"
            role="radio"
            aria-checked={active}
            title={t(`map.styles.${id}`)}
            aria-label={t(`map.styles.${id}`)}
            className={`flex size-8 items-center justify-center transition-colors ${active ? 'bg-brand text-white' : 'text-text-muted hover:bg-surface-hover hover:text-text'}`}
            onClick={() => setStyleId(id)}
          >
            <Icon size={15} />
          </button>
        );
      })}
    </div>
  );
}
